'use client';

import { useEffect, useState, useCallback } from 'react';
import { Box, VStack, HStack } from '@terra/ui-styled-system';
import { Button, Text } from '@terra/ui-design-system';
import {
  Bug01Icon,
  Building02Icon,
  ArrowDown01Icon,
  FingerPrintIcon,
  Key01Icon,
  Logout01Icon,
  RadioIcon,
  RefreshIcon,
  Shield01Icon,
  Timer01Icon,
  UserIcon,
  Cancel01Icon,
} from '@terra/ui-icons';

const STORAGE_KEY = 'debugSession';

type DebugAction = {
  label: string;
  icon: typeof Key01Icon;
  fn: () => Promise<unknown>;
};

function decodeJwtPreview(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split('.');

    if (parts.length !== 3) return null;

    return JSON.parse(atob(parts[1]));
  } catch {
    return null;
  }
}

export function SessionDebugMenu() {
  const [enabled, setEnabled] = useState(false);
  const [collapsed, setCollapsed] = useState(true);

  useEffect(() => {
    const check = () => {
      setEnabled(localStorage.getItem(STORAGE_KEY) === 'true');
    };

    check();
    const handleStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) check();
    };

    window.addEventListener('storage', handleStorage);

    return () => window.removeEventListener('storage', handleStorage);
  }, []);
  const api = typeof window !== 'undefined' ? window.electronAPI : null;
  const run = useCallback(async (label: string, fn: () => Promise<unknown>) => {
    console.group(`[SessionDebug] ${label}`);

    try {
      const result = await fn();

      console.log('Result:', result);
      console.groupEnd();
    } catch (error) {
      console.error('Error:', error);
      console.groupEnd();
    }
  }, []);

  if (!enabled || !api?.auth) return null;
  const authActions: DebugAction[] = [
    {
      label: 'isAuthenticated',
      icon: Shield01Icon,
      fn: () => api.auth?.isAuthenticated?.() ?? Promise.resolve(undefined),
    },
    {
      label: 'getAccessToken',
      icon: Key01Icon,
      fn: async () => {
        const result = await api.auth?.getAccessToken?.();

        if (result?.accessToken) {
          const payload = decodeJwtPreview(result.accessToken);
          const exp = payload?.exp as number | undefined;

          return {
            ...result,
            accessToken: `${result.accessToken.substring(0, 40)}...`,
            _decoded: {
              sub: payload?.sub,
              exp,
              expiresAt: exp ? new Date(exp * 1000).toISOString() : null,
              secondsUntilExpiry: exp
                ? exp - Math.floor(Date.now() / 1000)
                : null,
              dct: payload?.dct,
            },
          };
        }

        return result;
      },
    },
    {
      label: 'getIdToken',
      icon: FingerPrintIcon,
      fn: async () => {
        const result = await api.auth?.getIdToken?.();

        if (result?.idToken) {
          return {
            ...result,
            idToken: `${result.idToken.substring(0, 40)}...`,
          };
        }

        return result;
      },
    },
    {
      label: 'getProfile',
      icon: UserIcon,
      fn: () => api.auth?.getProfile?.() ?? Promise.resolve(undefined),
    },
    {
      label: 'getCurrentTenantId',
      icon: Building02Icon,
      fn: () => api.auth?.getCurrentTenantId?.() ?? Promise.resolve(undefined),
    },
    {
      label: 'getTenants',
      icon: Building02Icon,
      fn: () => api.auth?.getTenants?.() ?? Promise.resolve(undefined),
    },
    {
      label: 'refreshToken',
      icon: RefreshIcon,
      fn: () => api.auth?.refreshToken?.() ?? Promise.resolve(undefined),
    },
  ];
  const sessionActions: DebugAction[] = [
    {
      label: 'getInactivityMonitorEnabled',
      icon: RadioIcon,
      fn: () =>
        api.session?.getInactivityMonitorEnabled?.() ??
        Promise.resolve(undefined),
    },
    {
      label: 'getInactivityTime',
      icon: Timer01Icon,
      fn: async () => {
        const result = await api.session?.getInactivityTime?.();

        if (result?.remainingTime != null) {
          const mins = Math.floor(result.remainingTime / 60000);
          const secs = Math.floor((result.remainingTime % 60000) / 1000);

          return { ...result, _formatted: `${mins}m ${secs}s` };
        }

        return result;
      },
    },
    {
      label: 'recordActivity',
      icon: Timer01Icon,
      fn: () => api.session?.recordActivity?.() ?? Promise.resolve(undefined),
    },
    {
      label: 'extendSession',
      icon: Timer01Icon,
      fn: () => api.session?.extend?.() ?? Promise.resolve(undefined),
    },
    {
      label: 'logout',
      icon: Logout01Icon,
      fn: () => api.auth?.logout?.() ?? Promise.resolve(undefined),
    },
  ];

  return (
    <Box
      position="fixed"
      bottom="40px"
      right="12px"
      zIndex={9999}
      maxWidth="260px"
    >
      {collapsed ? (
        <Button size="xs" variant="outline" onClick={() => setCollapsed(false)}>
          <HStack gap={1}>
            <Bug01Icon size={14} />
            <Text fontSize="xs">Session Debug</Text>
          </HStack>
        </Button>
      ) : (
        <Box
          bg="bg.default"
          borderWidth="1px"
          borderColor="border.default"
          borderRadius="md"
          shadow="lg"
          p={3}
          maxHeight="70vh"
          overflowY="auto"
        >
          <HStack justifyContent="space-between" mb={2}>
            <HStack gap={1}>
              <Bug01Icon size={14} />
              <Text fontSize="xs" fontWeight="bold">
                Session Debug
              </Text>
            </HStack>
            <HStack gap={0}>
              <Button
                size="xs"
                variant="ghost"
                onClick={() => setCollapsed(true)}
              >
                <ArrowDown01Icon size={12} />
              </Button>
              <Button
                size="xs"
                variant="ghost"
                onClick={() => setEnabled(false)}
              >
                <Cancel01Icon size={12} />
              </Button>
            </HStack>
          </HStack>

          <Text fontSize="2xs" color="fg.muted" mb={1}>
            Auth
          </Text>
          <VStack gap={1} mb={3} alignItems="stretch">
            {authActions.map((action) => (
              <Button
                key={action.label}
                size="xs"
                variant="outline"
                onClick={() => run(action.label, action.fn)}
                w="full"
                justifyContent="flex-start"
              >
                <HStack gap={1}>
                  <action.icon size={12} />
                  <Text fontSize="2xs">{action.label}</Text>
                </HStack>
              </Button>
            ))}
          </VStack>

          <Text fontSize="2xs" color="fg.muted" mb={1}>
            Session
          </Text>
          <VStack gap={1} alignItems="stretch">
            {sessionActions.map((action) => (
              <Button
                key={action.label}
                size="xs"
                variant={action.label === 'logout' ? 'solid' : 'outline'}
                colorPalette={action.label === 'logout' ? 'red' : undefined}
                onClick={() => run(action.label, action.fn)}
                w="full"
                justifyContent="flex-start"
              >
                <HStack gap={1}>
                  <action.icon size={12} />
                  <Text fontSize="2xs">{action.label}</Text>
                </HStack>
              </Button>
            ))}
          </VStack>

          <Text fontSize="2xs" color="fg.subtle" mt={2} textAlign="center">
            Output → DevTools Console
          </Text>
        </Box>
      )}
    </Box>
  );
}
